<template>
  <div class="document-signing-view" :class="{ 'mobile': isMobile, 'mobile-signing-view': isMobile }">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="fa fa-spinner fa-spin"></i>
      <p>Loading document...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <i class="fa fa-exclamation-circle"></i>
      <h3>Error Loading Document</h3>
      <p>{{ error }}</p>
      <button class="btn btn-primary" @click="loadDocument">
        <i class="fa fa-redo"></i> Try Again
      </button>
    </div>

    <!-- Mobile View -->
    <div v-else-if="isMobile" class="mobile-content">
      <!-- Mobile Header -->
      <div class="mobile-signing-header">
        <h1 class="header-title">{{ documentName }}</h1>
      </div>

      <!-- Progress Tracker -->
      <div class="mobile-progress-section">
        <mobile-signing-progress :document-fields="documentFields" :completed-fields="allCompletedFields"
          :user-role="userRole" :show-breakdown="userRole !== 'student' && documentFields.length > 3" />
      </div>

      <!-- Inline document preview -->
      <div class="document-preview document-preview-mobile" v-if="pdfUrl">
        <PDFViewer
          :pdf-url="pdfUrl"
          @page-change="onPreviewPageChange"
          @scale-change="onPreviewScaleChange"
          @pdf-loaded="onPreviewPdfLoaded"
        >
          <template #annotations>
            <DocumentFieldMarker
              v-for="field in currentPagePreviewFields"
              :key="field.id"
              :field="field"
              :scale="previewScale"
              :signee-type="resolveSigneeType(field)"
              :completed="isFieldCompleted(field)"
              :disabled="!canUserSign(resolveSigneeType(field))"
              @click="handleFieldMarkerClick"
            />
          </template>
        </PDFViewer>
      </div>

      <!-- Completion Banner -->
      <div v-if="signatureStatus.is_fully_signed" class="completion-banner">
        <div class="completion-icon">
          <i class="fa fa-check"></i>
        </div>
        <h3 class="completion-title">All Done!</h3>
        <p class="completion-message">All fields have been completed.</p>
        <p class="completion-date" v-if="signatureStatus.fully_signed_at">
          Completed {{ formatTimestamp(signatureStatus.fully_signed_at) }}
        </p>
        <button v-if="canDownloadFlattened" class="btn-download-completed" @click="downloadFlattenedPdf"
          :disabled="downloadingPdf">
          <i :class="downloadingPdf ? 'fa fa-spinner fa-spin' : 'fa fa-download'"></i>
          {{ downloadingPdf ? 'Downloading...' : 'Download Completed PDF' }}
        </button>
      </div>

      <!-- Field Cards -->
      <div v-else class="mobile-field-list">
        <!-- Your Fields Section -->
        <div class="field-section" v-if="userFields.length > 0">
          <div class="section-header">
            <span class="section-title">Your Fields</span>
            <span class="section-count">{{ userCompletedCount }}/{{ userFields.length }}</span>
          </div>
          <mobile-field-card v-for="field in userFields" :key="field.id" :field="field"
            :is-completed="isFieldCompleted(field)" :is-current-user="true" @click="openMobileEditor(field)" />
        </div>

        <!-- Other Fields Section -->
        <div class="field-section" v-if="otherFields.length > 0">
          <div class="section-header">
            <span class="section-title">Other Fields</span>
            <span class="section-count">{{ otherCompletedCount }}/{{ otherFields.length }}</span>
          </div>
          <mobile-field-card v-for="field in otherFields" :key="field.id" :field="field"
            :is-completed="isFieldCompleted(field)" :is-current-user="false" @click="openMobileEditor(field)" />
        </div>
      </div>

      <!-- Mobile Field Editor (Full-Screen) -->
      <mobile-field-editor :visible="showMobileEditor" :field="currentField" :current-step="currentFieldIndex + 1"
        :total-steps="totalUserFields" :readonly="!canEditCurrentField" @save="saveFieldValue" @skip="skipToNextField"
        @back="handleMobileEditorBack" @close="closeMobileEditor" />
    </div>

    <!-- Desktop View (Original Layout) -->
    <div v-else class="signing-view-content">
      <!-- Header -->
      <div class="signing-header">
        <div class="header-info">
          <h2>{{ documentName }}</h2>
          <signature-status-badge :signature-required="signatureStatus.signature_required"
            :completion-percentage="signatureStatus.completion_percentage" :pending-count="pendingCount" size="large" />
        </div>

        <document-field-progress v-if="documentFields.length > 0"
          :completion-percentage="signatureStatus.completion_percentage" :requirements="signatureStatus.requirements"
          :completed-signatures="completedSignatures" :completed-fields="allCompletedFields" :show-details="true"
          :fields="documentFields" />
      </div>

      <!-- Inline document preview -->
      <div class="document-preview" v-if="pdfUrl">
        <PDFViewer
          :pdf-url="pdfUrl"
          @page-change="onPreviewPageChange"
          @scale-change="onPreviewScaleChange"
          @pdf-loaded="onPreviewPdfLoaded"
        >
          <template #annotations>
            <DocumentFieldMarker
              v-for="field in currentPagePreviewFields"
              :key="field.id"
              :field="field"
              :scale="previewScale"
              :signee-type="resolveSigneeType(field)"
              :completed="isFieldCompleted(field)"
              :disabled="!canUserSign(resolveSigneeType(field))"
              @click="handleFieldMarkerClick"
            />
          </template>
        </PDFViewer>
      </div>

      <!-- Main Layout -->
      <div class="signing-main">
        <!-- Fields Section -->
        <div class="fields-section">
          <!-- Pending Fields Panel -->
          <pending-fields-panel
            v-if="!signatureStatus.is_fully_signed && userPendingFields.length > 0"
            :pending-fields="userPendingFields"
            @field-click="handlePendingFieldClick" />

          <!-- Signature History -->
          <signature-history-list v-if="completedSignatures.length > 0" :signatures="completedSignatures"
            :show-thumbnails="false" :student-name="studentName" />

          <!-- Completion Message -->
          <div v-if="signatureStatus.is_fully_signed" class="completion-message">
            <i class="fa fa-check-circle"></i>
            <h4>Document Fully Signed!</h4>
            <p>All required signatures have been collected.</p>
            <p class="completion-date">
              Completed: {{ formatTimestamp(signatureStatus.fully_signed_at) }}
            </p>
            <button v-if="canDownloadFlattened" class="btn btn-success btn-download-desktop"
              @click="downloadFlattenedPdf" :disabled="downloadingPdf">
              <i :class="downloadingPdf ? 'fa fa-spinner fa-spin' : 'fa fa-download'"></i>
              {{ downloadingPdf ? 'Downloading...' : 'Download Completed PDF' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop Field Modal -->
    <field-modal v-if="!isMobile" :visible="showFieldModal" :field="currentField" :title="currentFieldTitle"
      :readonly="!canEditCurrentField" @close="closeFieldModal" @save="saveFieldValue" />
  </div>
</template>

<script>
import SignatureStatusBadge from './SignatureStatusBadge.vue';
import PendingFieldsPanel from './PendingFieldsPanel.vue';
import SignatureHistoryList from './SignatureHistoryList.vue';
import DocumentFieldProgress from './DocumentFieldProgress.vue';
import FieldModal from './FieldModal.vue';
import MobileFieldEditor from './MobileFieldEditor.vue';
import MobileSigningProgress from './MobileSigningProgress.vue';
import MobileFieldCard from './MobileFieldCard.vue';
import PDFViewer from './PDFViewer.vue';
import DocumentFieldMarker from './DocumentFieldMarker.vue';

export default {
  name: 'DocumentSigningView',
  components: {
    SignatureStatusBadge,
    PendingFieldsPanel,
    SignatureHistoryList,
    DocumentFieldProgress,
    FieldModal,
    MobileFieldEditor,
    MobileSigningProgress,
    MobileFieldCard,
    PDFViewer,
    DocumentFieldMarker
  },
  props: {
    documentId: {
      type: Number,
      required: true
    },
    userRole: {
      type: String,
      required: true,
      validator: (value) => [
        'student',
        'guardian',
        'instructor',
        'staff',
        'admin',
        'owner',
        'administrator'
      ].includes(value)
    },
    userId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      isMobile: false,
      documentName: '',
      pdfUrl: '',
      documentInstance: null,
      documentFields: [], // All fields from the document instance
      signatureFields: [], // Only signature type fields
      completedSignatures: [],
      showFieldModal: false,
      showMobileEditor: false,
      currentField: null,
      currentFieldIndex: 0,
      submittingField: false,
      downloadingPdf: false,
      previewPage: 1,
      previewScale: 1
    };
  },
  computed: {
    signatureStatus() {
      // Compute signature status from document instance
      if (!this.documentInstance) {
        return {
          signature_required: false,
          is_fully_signed: false,
          completion_percentage: 0,
          requirements: { document_level: [], field_level: {} }
        };
      }

      const allFields = this.documentFields;
      const signatureFields = this.signatureFields;

      if (allFields.length === 0) {
        return {
          signature_required: false,
          is_fully_signed: true,
          completion_percentage: 100,
          requirements: { document_level: [], field_level: {} }
        };
      }

      // Count completed fields (signatures check completedSignatures, others check value)
      const completedCount = allFields.filter(field => {
        const isSignature = field.annotations?.some(a => a.type === 'signature');
        if (isSignature) {
          return this.completedSignatures.some(sig => sig.document_field_id === field.id);
        } else {
          // For non-signature fields, check if value is filled
          return field.value !== null && field.value !== undefined && field.value !== '';
        }
      }).length;

      // Build requirements object from all fields
      const fieldLevelRequirements = {};
      allFields.forEach(field => {
        fieldLevelRequirements[field.id] = [this.resolveSigneeType(field)];
      });

      return {
        signature_required: signatureFields.length > 0,
        is_fully_signed: completedCount === allFields.length,
        completion_percentage: Math.round((completedCount / allFields.length) * 100),
        fully_signed_at: this.documentInstance.completed_at,
        requirements: {
          document_level: [],
          field_level: fieldLevelRequirements
        }
      };
    },

    pendingCount() {
      // Count all fields that aren't completed yet
      return this.documentFields.filter(field => {
        const isSignature = field.annotations?.some(a => a.type === 'signature');
        if (isSignature) {
          return !this.completedSignatures.some(sig => sig.document_field_id === field.id);
        } else {
          return field.value === null || field.value === undefined || field.value === '';
        }
      }).length;
    },

    allPendingSignatures() {
      // Get only signature fields that aren't signed yet
      return this.signatureFields.filter(field =>
        !this.completedSignatures.some(sig => sig.document_field_id === field.id)
      );
    },

    allCompletedFields() {
      // Get ALL fields that are completed (for progress tracking)
      return this.documentFields.filter(field => {
        const isSignature = field.annotations?.some(a => a.type === 'signature');
        if (isSignature) {
          return this.completedSignatures.some(sig => sig.document_field_id === field.id);
        } else {
          return field.value !== null && field.value !== undefined && field.value !== '';
        }
      });
    },

    userPendingSignatures() {
      // Get signature fields assigned to current user that aren't signed yet
      return this.signatureFields.filter(field => {
        // Check if field is assigned to current user
        const assignment = this.documentInstance?.user_assignments?.find(
          a => a.user_id === this.userId && a.document_field_id === field.id
        );
        if (!assignment) return false;

        // Check if not already signed
        return !this.completedSignatures.some(sig => sig.document_field_id === field.id);
      });
    },

    userSignatureFields() {
      // Return signature fields that the current user can sign
      return this.signatureFields.filter(field => {
        // Check if field's signee_type matches user role
        const annotation = field.annotations?.find(a => a.type === 'signature');
        if (!annotation) return false;

        return this.canUserSign(annotation.signee_type);
      });
    },

    // Mobile-specific computed properties
    userFields() {
      // Fields assigned to the current user
      return this.documentFields.filter(field =>
        this.canUserSign(this.resolveSigneeType(field))
      );
    },

    otherFields() {
      // Fields assigned to other roles
      return this.documentFields.filter(field =>
        !this.canUserSign(this.resolveSigneeType(field))
      );
    },

    userPendingFields() {
      // User's fields that aren't completed yet
      return this.userFields.filter(field => !this.isFieldCompleted(field));
    },

    totalUserFields() {
      // Total number of fields for the user (for step indicator)
      return this.userFields.length;
    },

    userCompletedCount() {
      return this.userFields.filter(field => this.isFieldCompleted(field)).length;
    },

    otherCompletedCount() {
      return this.otherFields.filter(field => this.isFieldCompleted(field)).length;
    },

    currentFieldTitle() {
      if (!this.currentField) return 'Edit Field';
      const label = this.currentField.field_label || this.currentField.label || this.currentField.field_name || this.currentField.name || 'Field';
      const annotation = this.currentField.annotations?.[0];
      const signeeType = annotation?.signee_type;
      if (signeeType) {
        return `${this.formatSigneeType(signeeType)}: ${label}`;
      }
      return label;
    },

    canEditCurrentField() {
      if (!this.currentField) return false;
      // Auto-filled fields are pre-populated and locked.
      if (this.currentField.read_only) return false;
      return this.canUserSign(this.resolveSigneeType(this.currentField));
    },

    studentName() {
      if (!this.documentInstance?.assigned_users) return '';
      const studentAssignment = this.documentInstance.assigned_users.find(
        a => a.role === 'student'
      );
      return studentAssignment?.user_name || '';
    },

    canDownloadFlattened() {
      // Can download flattened PDF when document is fully signed/completed
      return this.documentInstance?.status === 'completed' || this.signatureStatus.is_fully_signed;
    },

    currentPagePreviewFields() {
      // Fields visible on the page currently rendered in the preview.
      return this.documentFields.filter(field =>
        field.position
        && Number(field.page) === Number(this.previewPage)
      );
    }
  },
  mounted() {
    this.detectMobile();
    this.loadDocument();

    window.addEventListener('resize', this.detectMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.detectMobile);
  },
  methods: {
    detectMobile() {
      this.isMobile = window.innerWidth < 768;
    },

    // Single source of truth for who a field belongs to.
    // Order: explicit annotation signee, current assignment role, the
    // field's authored assignable_to, then 'unassigned'.
    resolveSigneeType(field) {
      if (!field) return 'unassigned';
      const annotation = field.annotations?.find(a => a.signee_type);
      return annotation?.signee_type
        || field.assigned_to?.role
        || field.assignable_to?.[0]
        || 'unassigned';
    },

    onPreviewPageChange(page) {
      this.previewPage = page;
    },
    onPreviewScaleChange(scale) {
      this.previewScale = scale;
    },
    onPreviewPdfLoaded() {
      this.previewPage = 1;
    },

    async loadDocument() {
      this.loading = true;
      this.error = null;

      try {
        // Load document instance using DocumentInstanceModel
        await this.fetchDocumentInstance();

        // Load document fields
        await this.fetchDocumentFields();

        // Load completed signatures
        await this.fetchCompletedSignatures();

      } catch (err) {
        console.error('Error loading document:', err);
        this.error = err.message || 'Failed to load document';
      } finally {
        this.loading = false;
      }
    },

    async fetchDocumentInstance() {
      // Load document instance with DocumentInstanceModel
      const instance = await this.$store.dispatch('documents/GET_DOCUMENT', this.documentId);
      this.documentInstance = instance;

      // Get document name from template
      this.documentName = instance.template?.name || 'Untitled Document';
      // Get PDF URL
      this.pdfUrl = instance.file_url || '';
    },

    async fetchDocumentFields() {
      // Get all fields from document instance
      this.documentFields = this.documentInstance?.document_fields || [];

      // Filter to get only signature fields
      this.signatureFields = this.documentFields.filter(field => {
        // Check if field has a signature annotation
        const annotations = field.annotations || [];
        return annotations.some(a => a.type === 'signature');
      });
    },

    async fetchCompletedSignatures() {
      // Get signatures for this document instance
      const signaturesData = await this.$store.dispatch('documents/GET_SIGNATURES', this.documentId);
      // Extract field-level signatures - these are the ones with document_field_id
      const signatures = signaturesData.signatures || {};
      this.completedSignatures = signatures.field_level || [];
    },

    canUserSign(signeeType) {
      // Exact role-to-signee match (e.g. student signing a student field).
      if (this.userRole === signeeType) {
        return true;
      }

      // Staff umbrella: staff, owner, and administrator are treated as
      // interchangeable for the purpose of filling non-signer fields.
      // They can sign fields explicitly assigned to 'staff' and fields
      // with no signee assignment (unassigned). Matches the isStaff()
      // check in drivecoach.run.ts.
      const isStaffUmbrella =
        this.userRole === 'staff' ||
        this.userRole === 'owner' ||
        this.userRole === 'administrator';
      if (isStaffUmbrella && (signeeType === 'staff' || !signeeType || signeeType === 'unassigned')) {
        return true;
      }

      return false;
    },

    isUserSignatureField(field) {
      // Check if field's signee_type matches user role
      const annotation = field.annotations?.find(a => a.type === 'signature');
      if (!annotation) return false;
      return this.canUserSign(annotation.signee_type);
    },

    isSignatureCompleted(field) {
      // Check if this field has a completed signature
      return this.completedSignatures.some(sig => sig.document_field_id === field.id);
    },

    isFieldCompleted(field) {
      const isSignature = field.annotations?.some(a => a.type === 'signature');
      if (isSignature) {
        return this.completedSignatures.some(sig => sig.document_field_id === field.id);
      } else {
        return field.value !== null && field.value !== undefined && field.value !== '';
      }
    },

    getSignatureFieldStyle(field) {
      // Get the signature annotation from field
      const annotation = field.annotations?.find(a => a.type === 'signature');
      if (!annotation) return {};

      // Convert annotation coordinates to overlay positioning
      return {
        position: 'absolute',
        left: annotation.rect.x + 'px',
        top: annotation.rect.y + 'px',
        width: annotation.rect.width + 'px',
        height: annotation.rect.height + 'px'
      };
    },

    handleSignatureFieldClick(field) {
      if (this.isSignatureCompleted(field)) {
        // Already signed, do nothing or show message
        return;
      }

      this.openFieldModal(field);
    },

    handlePendingFieldClick(field) {
      // Check if this is a signature field that's already been signed
      const isSignature = field.annotations?.some(a => a.type === 'signature');
      if (isSignature && this.isSignatureCompleted(field)) {
        // Already signed, don't open modal
        return;
      }

      // Open the field modal for any field type
      this.openFieldModal(field);
    },

    openFieldModal(field) {
      this.currentField = field;
      this.showFieldModal = true;
    },

    closeFieldModal() {
      this.showFieldModal = false;
      this.currentField = null;
    },

    // Mobile-specific methods
    openMobileEditor(field) {
      if (this.isFieldCompleted(field)) {
        return;
      }

      this.currentField = field;
      // Calculate current index within user's fields
      const userFieldIndex = this.userFields.findIndex(f => f.id === field.id);
      this.currentFieldIndex = userFieldIndex >= 0 ? userFieldIndex : 0;
      this.showMobileEditor = true;
    },

    closeMobileEditor() {
      this.showMobileEditor = false;
      this.submittingField = false;
    },

    handleMobileEditorBack() {
      if (this.currentFieldIndex > 0) {
        this.goToPreviousField();
      } else {
        this.closeMobileEditor();
      }
    },

    goToNextField() {
      const pendingFields = this.userPendingFields;
      const currentIndex = pendingFields.findIndex(f => f.id === this.currentField?.id);

      if (currentIndex >= 0 && currentIndex < pendingFields.length - 1) {
        // Go to next pending field
        const nextField = pendingFields[currentIndex + 1];
        this.currentField = nextField;
        this.currentFieldIndex = this.userFields.findIndex(f => f.id === nextField.id);
      } else {
        // No more pending fields, close editor
        this.closeMobileEditor();
      }
    },

    goToPreviousField() {
      const currentIndex = this.userFields.findIndex(f => f.id === this.currentField?.id);
      if (currentIndex > 0) {
        const prevField = this.userFields[currentIndex - 1];
        this.currentField = prevField;
        this.currentFieldIndex = currentIndex - 1;
      }
    },

    skipToNextField() {
      this.goToNextField();
    },

    handleFieldMarkerClick(field) {
      if (this.isMobile) {
        this.openMobileEditor(field);
      } else {
        this.openFieldModal(field);
      }
    },

    async saveFieldValue({ field, value, fieldType }) {
      if (this.submittingField) return;

      this.submittingField = true;

      try {
        const annotation = field.annotations?.[0];

        if (fieldType === 'signature') {
          // Handle signature save
          const requestData = {
            signature_data: value,
            signee_type: annotation?.signee_type,
            document_field_id: field.id,
            annotation_id: annotation?.id
          };

          await this.$store.dispatch('documents/ADD_SIGNATURE', {
            documentId: this.documentId,
            signatureData: requestData
          });

          // Reload signatures to update view
          await this.fetchCompletedSignatures();

          // Emit event for controller
          this.$emit('signature-added', requestData);
        } else {
          // Handle other field types
          await this.$store.dispatch('documents/UPDATE_FIELD', {
            documentId: this.documentId,
            fieldName: field.field_name || field.name,
            value: value
          });

          // Update local field value
          const fieldIndex = this.documentFields.findIndex(f => f.id === field.id);
          if (fieldIndex !== -1) {
            this.$set(this.documentFields[fieldIndex], 'value', value);
          }

          // Emit event for controller
          this.$emit('field-updated', { field, value });
        }

        // For mobile, auto-advance to next field
        if (this.isMobile) {
          this.submittingField = false;
          this.goToNextField();
        } else {
          // Close modal on desktop
          this.closeFieldModal();
        }

      } catch (err) {
        console.error('Error saving field:', err);
        alert('Failed to save. Please try again.');
      } finally {
        this.submittingField = false;
      }
    },

    formatSigneeType(type) {
      const labels = {
        student: 'Student',
        guardian: 'Guardian',
        instructor: 'Instructor',
        staff: 'Staff',
        admin: 'Admin'
      };
      return labels[type] || type;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    async downloadFlattenedPdf() {
      if (this.downloadingPdf) return;

      this.downloadingPdf = true;

      try {
        const pdfData = await this.$store.dispatch('documents/GET_FLATTENED_PDF', this.documentId);
        if (pdfData && pdfData.file_url) {
          window.open(pdfData.file_url, '_blank');
        } else {
          alert('Completed PDF not yet available. Please try again later.');
        }
      } catch (err) {
        console.error('Error downloading flattened PDF:', err);
        alert('Failed to download completed PDF. Please try again.');
      } finally {
        this.downloadingPdf = false;
      }
    }
  }
};
</script>

<style scoped>
.document-signing-view {
  width: 100%;
  min-height: 100vh;
  background-color: #f7fafc;
}

/* Loading & Error States */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px 20px;
}

.loading-state i {
  font-size: 48px;
  color: #4299e1;
  margin-bottom: 16px;
}

.loading-state p {
  font-size: 16px;
  color: #718096;
}

.error-state i {
  font-size: 64px;
  color: #ef4444;
  margin-bottom: 16px;
}

.error-state h3 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #2d3748;
}

.error-state p {
  margin: 0 0 24px 0;
  font-size: 14px;
  color: #718096;
}

/* Desktop Main Content */
.signing-view-content {
  max-width: 1400px;
  padding: 0px;
}

.signing-header {
  background-color: transparent;
  border-radius: 8px;
  padding: 0px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-info h2 {
  margin: 0;
  font-size: 24px;
  color: #2d3748;
}

.document-preview {
  margin-bottom: 20px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 70vh;
  min-height: 400px;
}

.document-preview > * {
  height: 100%;
}

.signing-main {
  display: flex;
  gap: 20px;
}

.fields-section {
  flex: 1;
}

.fields-section .completion-message,
.fields-section .pending-fields-panel,
.fields-section .signature-history-list {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.fields-section .signature-history-list {
  margin-top: 2em;
}

/* Legacy PDF styles - kept for potential future use */
.pdf-section {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: none;
}

.pdf-container {
  position: relative;
  min-height: 600px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}

.pdf-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #a0aec0;
}

.pdf-placeholder i {
  font-size: 96px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.pdf-placeholder p {
  margin: 4px 0;
  font-size: 14px;
}

.doc-url {
  font-size: 12px;
  font-family: monospace;
  color: #cbd5e0;
}

/* Signature Overlays */
.signature-overlay {
  border: 3px dashed;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
}

.signature-overlay.pending {
  border-color: #f59e0b;
  background-color: rgba(252, 211, 77, 0.1);
}

.signature-overlay.pending.current-user {
  border-color: #4299e1;
  background-color: rgba(66, 153, 225, 0.1);
  animation: pulse 2s infinite;
}

.signature-overlay.pending.current-user:hover {
  border-color: #2b6cb0;
  background-color: rgba(66, 153, 225, 0.2);
}

.signature-overlay.completed {
  border-color: #10b981;
  border-style: solid;
  background-color: rgba(16, 185, 129, 0.1);
  cursor: default;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
}

.overlay-content i {
  font-size: 24px;
}

.signature-overlay.pending .overlay-content i {
  color: #f59e0b;
}

.signature-overlay.pending.current-user .overlay-content i {
  color: #4299e1;
}

.signature-overlay.completed .overlay-content i {
  color: #10b981;
}

.overlay-label {
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.6;
  }
}

/* Sidebar */
.sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.completion-message {
  background-color: #d1fae5;
  border: 2px solid #10b981;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  margin-top: 2em;
}

.completion-message>i {
  font-size: 48px;
  color: #10b981;
  margin-bottom: 12px;
  display: block;
}

.completion-message h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #065f46;
}

.completion-message p {
  margin: 4px 0;
  font-size: 14px;
  color: #047857;
}

.completion-date {
  font-size: 12px;
  color: #059669;
  font-style: italic;
}

/* Mobile Content Styles */
.mobile-content {
  min-height: 100vh;
  background-color: var(--ds-signing-bg, #F8FAFC);
}

.mobile-signing-header {
  background-color: var(--ds-signing-card, #FFFFFF);
  padding: 16px;
  padding-top: calc(16px + env(safe-area-inset-top, 0));
  border-bottom: 1px solid var(--ds-signing-border, #E2E8F0);
  position: sticky;
  top: 0;
  z-index: 100;
}

.mobile-signing-header .header-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--ds-signing-text, #1E293B);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-progress-section {
  padding: 16px;
}

/* Mobile inline document preview */
.document-preview-mobile {
  height: 55vh;
  min-height: 320px;
  margin: 0 16px 16px 16px;
  border-radius: 12px;
}

/* Completion Banner */
.completion-banner {
  margin: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  border-radius: 16px;
  text-align: center;
  color: white;
}

.completion-banner .completion-icon {
  width: 64px;
  height: 64px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 32px;
}

.completion-banner .completion-title {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 700;
}

.completion-banner .completion-message {
  margin: 0;
  font-size: 14px;
  color: white;
  background-color: rgba(255, 255, 255, 0.15);
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
}

.completion-banner .completion-date {
  margin-top: 12px;
  font-size: 12px;
  opacity: 0.8;
  font-style: normal;
  color: white;
}

/* Download Completed PDF Button - Mobile */
.btn-download-completed {
  margin-top: 16px;
  padding: 12px 24px;
  background-color: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-download-completed:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.6);
}

.btn-download-completed:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Download Completed PDF Button - Desktop */
.btn-download-desktop {
  margin-top: 16px;
  width: 100%;
  background-color: #065f46;
  border-color: #065f46;
  color: white;
  padding: 10px 20px;
  font-weight: 600;
}

.btn-download-desktop:hover:not(:disabled) {
  background-color: #047857;
  border-color: #047857;
  color: white;
}

.btn-download-desktop:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-download-desktop i {
  margin-right: 8px;
}

/* Mobile Field List */
.mobile-field-list {
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0));
}

.field-section {
  margin-top: 8px;
}

.field-section:first-child {
  margin-top: 0;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 0 4px;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--ds-signing-muted, #64748B);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-count {
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-signing-text, #1E293B);
  background-color: var(--ds-signing-border, #E2E8F0);
  padding: 2px 8px;
  border-radius: 10px;
}

/* Desktop Mobile Optimization */
@media (max-width: 768px) {
  .document-signing-view.mobile .signing-view-content {
    padding: 12px;
  }

  .document-signing-view.mobile .signing-header {
    padding: 16px;
  }

  .document-signing-view.mobile .header-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .document-signing-view.mobile .header-info h2 {
    font-size: 20px;
  }

  .document-signing-view.mobile .signing-main {
    flex-direction: column;
  }

  .document-signing-view.mobile .pdf-container {
    min-height: 400px;
  }
}
</style>
